// Хелперы базы знаний: перенос проверенной логики прежнего приложения.
import type { Bootstrap, ProcessLite } from '../api';

export const CM_EMOJI = ['👍', '❤️', '🔥', '👏', '😁', '🤔'];

export function initials(name: string): string {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '?';
  if (w.length === 1) return w[0].charAt(0).toUpperCase();
  return (w[0].charAt(0) + w[1].charAt(0)).toUpperCase();
}

export function cmTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function plural(n: number, forms: [string, string, string]): string {
  const a = n % 10;
  const b = n % 100;
  const f = a === 1 && b !== 11 ? forms[0] : a >= 2 && a <= 4 && (b < 10 || b >= 20) ? forms[1] : forms[2];
  return `${n} ${f}`;
}

// Сворачивание полных должностей в аббревиатуры (тумблер «должности»).
const ROLE_COLLAPSE: [RegExp, string][] = [
  [/[Зз]аместител[а-яё]+ руководителя строительной площадки/g, 'ПОМ'],
  [/[Рр]уководител[а-яё]+ строительной площадки/g, 'РС'],
  [/[Мм]енеджер[а-яё]* проекта/g, 'МП'],
  [/[Дд]иректор[а-яё]* по проектам/g, 'ДП'],
  [/[Дд]иректор[а-яё]* службы (?:технического заказчика|техзаказчика)/g, 'ДТЗ'],
  [/[Сс]троительн[а-яё]+ контрол[а-яё]+/g, 'СК'],
  [/[Сс]тройконтрол[а-яё]+/g, 'СК'],
  [/[Сс]метчик[а-яё]*/g, 'СМ'],
  [/[Зз]акупщик[а-яё]*/g, 'ЗАК'],
  [/[Ии]нженер[а-яё]* ПТО/g, 'ПТО'],
  [/[Фф]инансов[а-яё]+ директор[а-яё]*/g, 'ФД'],
  [/[Сс]пециалист[а-яё]* по охране труда/g, 'ОТ'],
  [/[Кк]ладовщик[а-яё]*/g, 'СКЛ'],
];

export function collapseRoles(html: string): string {
  let out = html;
  for (const [rx, ab] of ROLE_COLLAPSE) out = out.replace(rx, ab);
  return out;
}

/*
 * Термин целым словом. Левая граница — группа, а не обратный просмотр
 * `(?<!…)`: Safari научился ему только в версии 16.4, а на iPhone 6s, 7 и SE
 * первого поколения стоит iOS 15 и обновиться они не могут. Там выражение
 * падало прямо при создании, и страница процесса не открывалась вовсе.
 *
 * Плата за это — символ слева входит в совпадение, и вернуть его обязан тот,
 * кто выражением пользуется. Группы: 1 — граница слева, 2 — сам термин.
 */
export function termRegex(terms: Bootstrap['terms']): RegExp {
  const keys = terms.map((t) => t.abbr).filter((k) => k !== 'КС-6');
  return new RegExp(
    `(^|[^А-ЯЁа-яёA-Za-z0-9-])(КС-6|М-15|${keys.join('|')})(?![А-ЯЁа-яёA-Za-z0-9])`,
    'g',
  );
}

/** Расшифровка терминов при первом упоминании (тумблер «термины полностью»). */
export function expandTerms(html: string, terms: Bootstrap['terms']): string {
  const dict = Object.fromEntries(terms.map((t) => [t.abbr, t.definition]));
  const seen = new Set<string>();
  // только в текстовых узлах: делим по тегам
  return html
    .split(/(<[^>]+>)/)
    .map((chunk) => {
      if (chunk.startsWith('<')) return chunk;
      return chunk.replace(termRegex(terms), (_all: string, before: string, term: string) => {
        if (!dict[term] || seen.has(term)) return `${before}${term}`;
        seen.add(term);
        return `${before}${term} (${dict[term]})`;
      });
    })
    .join('');
}

export function foundTerms(text: string, terms: Bootstrap['terms']): string[] {
  const dict = Object.fromEntries(terms.map((t) => [t.abbr, t.definition]));
  const plain = text.replace(/<[^>]+>/g, ' ');
  // берём вторую группу: в само совпадение входит ещё и граница слева
  const found = [...new Set([...plain.matchAll(termRegex(terms))].map((m) => m[2]))];
  return found.filter((t) => dict[t]);
}

export type RoleIndex = Record<string, { D: string[]; S: string[]; O: string[]; I: string[] }>;

export function buildRoleIndex(bootstrap: Bootstrap): RoleIndex {
  const idx: RoleIndex = {};
  for (const r of bootstrap.roles) idx[r.code] = { D: [], S: [], O: [], I: [] };
  const keyMap: Record<string, keyof RoleIndex[string]> = { Д: 'D', С: 'S', О: 'O', И: 'I' };
  for (const p of bootstrap.processes) {
    for (const [k, codes] of Object.entries(p.dsi)) {
      const key = keyMap[k];
      if (!key) continue;
      for (const c of codes) if (idx[c]) idx[c][key].push(p.code);
    }
  }
  return idx;
}

export function roleNames(codes: string[], full: boolean, roles: Bootstrap['roles']): string {
  if (!full) return codes.join(', ');
  const map = Object.fromEntries(roles.map((r) => [r.code, r.name]));
  return codes.map((c) => map[c] ?? c).join(', ');
}

export function byCode(bootstrap: Bootstrap): Record<string, ProcessLite> {
  return Object.fromEntries(bootstrap.processes.map((p) => [p.code, p]));
}
